import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { orderLineService } from "/app/src/services";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { OrderLine as OrderLineType } from "/app/src/models";
import { useHandleNavigationError } from "/app/src/helpers/api";
import Breadcrumbs from "/app/src/components/generic/components/breadcrumbs";
import InfoList from "/app/src/components/generic/components/explorer/infoList";
import { PowerPickTimeZoneToLocal } from "/app/src/components/generic/formatting/dateTime";
import { useState } from "react";

const ORDER_LINE_DYNAMIC_IGNORE_KEYS = new Set<string>([
  "id",
  "number",
  "orderId",
  "costCenterName",
  "materialName",
  "quantity",
  "lot",
  "qualification",
  "modifiedDate",
  "productionDate",
  "releaseDate",
  "expiryDate",
  "serialNumber",
  "pickLocation",
  "hostIdentification",
  "Info1",
  "Info2",
  "Info3",
  "Info4",
  "Info5",
  "transactions",
  "locations",
  "storage_units",
  "materialId",
  "quantityRequested",
  "quantityConfirmed",
  "quantityDeviated",
  "progressType",
  "status",
  "putDate",
  "pickBinName",
  "putBinName",
  "putHeight",
  "owner",
  "hasStock",
  "directionType",
  "incompleteAllocations",
  "orderName",
  "putLocation",
  "storageUnits",
  "allocatedQuantity",
]);

/**
 * OrderLine component fetches and displays details for a single order line.
 *
 * @returns {JSX.Element} The rendered OrderLine component.
 */
export default function OrderLine() {
  const { t } = useTranslation();
  const { orderLineId } = useParams();
  const handleNavigationError = useHandleNavigationError();
  const [dynamicFields, setDynamicFields] = useState<
    Array<{ label: string; value: string }>
  >([]);

  // Get the Single OrderLine from URL
  const { data: orderLine } = useQuery({
    queryKey: ["orderLine", orderLineId],
    queryFn: () => {
      return orderLineService.getSingle(orderLineId).catch(() => {
        handleNavigationError();
      });
    },
    initialData: { order_line: {} },
    retry: false,
    select: (data: { order_line: OrderLineType }) => {
      // Return the actual order_line so `data` is the OrderLine object
      return data.order_line;
    },
    onSuccess: (data?: OrderLineType) => {
      if (!data) return;

      const newDynamicFields = Object.entries(data)
        .filter(
          ([key, value]) =>
            !ORDER_LINE_DYNAMIC_IGNORE_KEYS.has(key) && value !== null,
        )
        .map(([key, value]) => ({ label: key, value: String(value) }));

      setDynamicFields(newDynamicFields);
    },
  });

  const basicInfo = [
    { label: t("translation:number"), value: orderLine?.number },
    {
      label: t("translation:creation_date"),
      value: (
        <PowerPickTimeZoneToLocal
          date={orderLine?.modifiedDate}
          format={"MMM DD, YYYY HH:mm"}
        />
      ),
    },
    { label: t("translation:status"), value: orderLine?.status },
    {
      label: t("translation:order_name"),
      value: orderLine?.orderName,
      link: `/explorer/orders/${orderLine?.orderId}`,
    },
  ];

  const infoFields = [
    { label: t("translation:info1"), value: orderLine?.Info1 },
    { label: t("translation:info2"), value: orderLine?.Info2 },
    { label: t("translation:info3"), value: orderLine?.Info3 },
    { label: t("translation:info4"), value: orderLine?.Info4 },
    { label: t("translation:info5"), value: orderLine?.Info5 },
  ];

  const materialInfo = [
    {
      label: t("translation:material_name"),
      value: orderLine?.materialName,
      link: `/explorer/materials/${orderLine?.materialId}`,
    },
    {
      label: t("translation:quantity_requested"),
      value: orderLine?.quantityRequested,
    },
    {
      label: t("translation:allocated_quantity"),
      value: orderLine?.allocatedQuantity,
    },
  ];

  const locationInfo = [
    { label: t("translation:lot_number"), value: orderLine?.lot },
    { label: t("translation:serial_number"), value: orderLine?.serialNumber },
  ];

  // Only include pick/put location entries per rules:
  // - If pick exists and put is missing => include pick only
  // - If put exists and pick is missing => include put only
  // - If both exist => include both
  if (orderLine?.pickLocation && !orderLine?.putLocation) {
    locationInfo.push({
      label: t("translation:pick_location"),
      value: orderLine.pickLocation,
    });
  } else if (!orderLine?.pickLocation && orderLine?.putLocation) {
    locationInfo.push({
      label: t("translation:put_location"),
      value: orderLine.putLocation,
    });
  } else {
    if (orderLine?.pickLocation) {
      locationInfo.push({
        label: t("translation:pick_location"),
        value: orderLine.pickLocation,
      });
    }
    if (orderLine?.putLocation) {
      locationInfo.push({
        label: t("translation:put_location"),
        value: orderLine.putLocation,
      });
    }
  }

  return (
    <div className="explorer">
      <Helmet>
        <title>{`Order Line ${orderLine?.number} - ItemPath`}</title>
      </Helmet>
      <Breadcrumbs
        items={[
          { label: "Explorer", to: "/explorer" },
          { label: "Order Lines", to: "/explorer/order_lines" },
          { label: orderLine?.id },
        ]}
      />
      <h1 className="text-[36px] font-medium">{orderLine?.orderName}</h1>
      <h2 className="text-[16px] mb-6 text-neutral-smoke">
        Order Line Id: {orderLine?.id}
      </h2>
      <InfoList
        header={t("translation:basic_info")}
        items={basicInfo}
        infoFields={infoFields}
      />
      <InfoList header={t("translation:material_info")} items={materialInfo} />
      <InfoList header={t("translation:location_info")} items={locationInfo} />
      {dynamicFields.length > 0 && (
        <InfoList
          header={t("translation:dynamic_fields")}
          items={dynamicFields}
        />
      )}
    </div>
  );
}
